// Single source for the FAQ block. AiGenerator renders these as the visible
// accordion and page.js builds the FAQPage structured data from the same array —
// Google requires the answer text in the markup to match what a visitor can read,
// and answer engines quote whichever copy they find first.
//
// Every number here is asserted by the code: the generation price and the free
// edits come from utils/aiPricing.js (the fallback of GET /quota → pricing),
// 1 credit per import from onConvertImport, the 9 MB / PNG-JPG-WEBP-SVG limits
// from validateImportFile, and 1000 characters from the prompt textarea. Keep
// them in step if the tariff changes.
import {
  AI_EDIT_PACK_CREDITS, AI_EDIT_PACK_SIZE, AI_EDITS_INCLUDED, AI_GENERATION_CREDITS, SIGNUP_CREDITS,
  SIGNUP_CREDITS_DAYS, formatCredits, signupCreditsReach,
} from '../../utils/aiPricing';

const GEN = formatCredits(AI_GENERATION_CREDITS);
const EDIT_PACK = formatCredits(AI_EDIT_PACK_CREDITS);

const FAQ = [
  {
    id: 'faq-what',
    q: 'What exactly do I get at the end?',
    a: 'A .svghmi file — the widget format Siemens WinCC Unified uses. You download it and add it to your project like any other widget. It is not a picture: the geometry is real SVG, and the dynamic properties are already wired up as bindings you can drive from tags.',
  },
  {
    id: 'faq-opens',
    q: 'Will the widget actually open in WinCC?',
    a: 'Yes — that is checked before you ever see it. Every generated file goes through structural validation and is rendered on our side; if something is off, the AI fixes it and validates again. You only get a file that passed. The badge above the preview tells you whether it validated cleanly or needed an auto-fix.',
  },
  {
    id: 'faq-cost',
    q: 'How many credits does this cost?',
    a: `Generating a widget costs ${GEN}, charged when the generation starts and refunded automatically if it fails. Its first ${AI_EDITS_INCLUDED} edits are included; after that ${EDIT_PACK} opens ${AI_EDIT_PACK_SIZE} more. Questions about the widget are always free, and an edit that changes nothing costs nothing. Switching an equipment drawing to another style is free. Importing an image or SVG costs 1 credit. Every credit pack pays the same per generation; larger packs are simply cheaper per credit.`,
  },
  {
    id: 'faq-failed',
    q: 'What if the generation fails or I do not like the result?',
    a: `If a generation fails on our side, the credits are returned to your balance automatically — you will see a note saying so. If it simply is not what you pictured, describe the change in the chat beside the preview and it edits the widget you already have instead of starting over; the first ${AI_EDITS_INCLUDED} edits are included. Equipment drawings and icons arrive as three variants to pick from, and for other widgets you can ask for two more variants once, free. A fresh generation of the same prompt costs ${GEN}.`,
  },
  {
    id: 'faq-edit',
    q: 'Can I change the widget after it is generated?',
    a: 'In three ways. Chat with it — "make the needle red", "set the range to 0–200", "what properties does it have?" — and it edits the same widget step by step, keeping everything you liked. Adjust the dynamic properties directly in the preview to see how it looks at different values. Or open the layout editor to move and delete individual elements. Every revision is kept, so you can always go back to an earlier one.',
  },
  {
    id: 'faq-import',
    q: 'What is the difference between describing a widget and importing an image?',
    a: 'Describing gives you an interactive widget with dynamic properties you can bind to tags. Importing traces an existing PNG, JPG, WEBP or SVG (up to 9 MB) into clean SVGHMI geometry, but the result is static — it will look right and render fast, without bindings. Use import when you already have artwork or a company symbol; describe when you need the widget to react to process values.',
  },
  {
    id: 'faq-prompt',
    q: 'How should I write the prompt?',
    a: 'Concrete beats poetic. Name the type of widget, the ranges and units, the colours and what should be shown as text — for example "a 270° pressure gauge, 0–10 bar, green-amber-red zones, needle, value in the centre". You have 1000 characters. If you are unsure, click one of the "What you can make" tiles under the prompt box to load a prompt that works and edit it from there.',
  },
  {
    id: 'faq-creativity',
    q: 'What do Precise, Balanced and Creative change?',
    a: 'How much licence the AI takes with your description. Precise sticks closely to what you wrote and is the safest choice for standard instrumentation and P&ID symbols. Creative allows more interpretation of styling and detail, which helps for dashboard-style widgets. Balanced is the default and suits most work.',
  },
  {
    id: 'faq-expire',
    q: 'Do credits expire?',
    a: `Purchased credits do not: they stay on your account until you spend them — there is no monthly reset and no subscription. The ${SIGNUP_CREDITS} free credits a new account gets are valid for ${SIGNUP_CREDITS_DAYS} days; any purchase makes all your credits permanent.`,
  },
  {
    id: 'faq-access',
    q: 'Do I need to buy anything to try the generator?',
    a: `No. Sign up free and get ${SIGNUP_CREDITS} credits on us — no card, nothing to enter but your email — ${signupCreditsReach()}. They work in the generator and in the converter alike and are valid for ${SIGNUP_CREDITS_DAYS} days. The line under the Generate button shows your balance. A generation costs ${GEN}; when the free credits run out, any credit pack works.`,
  },
];

export default FAQ;
